// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.

export const TECHNICAL_POLICY_COLUMNS = {
  Gisement: (attributes, isDisabled, attributeValues) => [
    {
      field: 'Attribut',
      label: 'Attribut',
      inputType: 'select',
      options: attributes,
      required: true,
    },
    {
      field: 'Operateur',
      label: 'Opérateur',
      inputType: 'select',
      options: [
        { label: 'IN', value: 'IN' },
        { label: '<', value: 'LT' },
        { label: '≤', value: 'LEQ' },
        { label: '>', value: 'GT' },
        { label: '≥', value: 'GEQ' },
      ],
      disabled: isDisabled,
      defaultValue: 'IN',
      required: true,
    },
    {
      field: 'Valeurs',
      label: 'Valeur',
      inputType: (row) => (row?.Operateur === 'IN' ? 'autocomplete' : 'number'),
      options: [],
      getOptions: attributeValues,
      required: true,
      autocompleteProps: {
        freeSolo: true,
        id: 'gisement-valeurs-autocomplete',
        checkbox: false,
        disableCloseOnSelect: true,
      },
    },
    {
      field: 'id',
      isInvisible: true,
    },
  ],
  NiveauDePriorisation: (isIdentifierUnique, isPrioritizationRankUnique) => [
    {
      field: 'id',
      isInvisible: true,
    },
    {
      field: 'Identifiant',
      label: 'Niveau',
      required: true,
      valid: isIdentifierUnique,
      errorMessage: "L'identifiant existe déjà",
      getDefaultValue: (existingRows) => {
        const visibleLevelsCount = existingRows ? existingRows.length : 0;
        return `Level${visibleLevelsCount + 1}`;
      },
    },
    {
      field: 'RangPriorisation',
      label: 'Rang de priorisation',
      inputType: 'number',
      required: true,
      min: 1,
      step: 1,
      valid: isPrioritizationRankUnique,
      errorMessage: 'Le rang de priorisation existe déjà',
      getDefaultValue: (existingRows) => existingRows?.length + 1 || 1,
    },
    {
      field: 'SchemaPriorisation',
      label: 'Schema de priorisation',
      defaultValue: 'ConsequenceBased',
      inputType: 'select',
      options: ['ConsequenceBased', 'OpportunityBased', 'ProbabilityBased', 'RiskBased'],
      required: true,
    },
  ],
  ReglesGestion: () => [
    {
      field: 'Identifiant',
    },
    {
      field: 'TypePriorisation',
    },
    {
      field: 'NiveauDePriorisation',
    },
    {
      field: 'AnneeDebut',
    },
    {
      field: 'AnneeFin',
    },
    {
      field: 'CategoriesBudgetaires',
    },
    {
      field: 'SequenceActions1',
    },
    {
      field: 'SequenceActions2',
    },
    {
      field: 'NbIterationSequenceActions1',
    },
    { field: 'ReevaluationAnnuelleCriteres' },
    { field: 'Desactiver' },
    { field: 'FormuleCritere' },
  ],
  CriteresDeclenchement: (isOperateurDisabled, isValeurReferenceDisabled, isValid, attributeValues, family, t) => [
    {
      field: 'id',
      isInvisible: true,
    },
    {
      field: 'RegleGestion',
      isInvisible: true,
    },
    {
      field: 'Identifiant',
      label: 'Identifiant',
      inputType: 'text',
      valid: isValid,
      required: true,
      errorMessage: "L'identifiant existe déjà",
      getDefaultValue: (existingRows) => {
        const visibleCriteriaCount = existingRows ? existingRows.length : 0;
        return `C${visibleCriteriaCount + 1}`;
      },
    },
    {
      field: 'FamillesEquipements',
      label: "Familles d'équipements",
      inputType: 'multiSelect',
      options: family,
    },
    {
      field: 'SeuilValidation',
      label: 'Seuil Validation',
      info: ' (valeur entre 0 et 1), 0 correspond à au moins un actif',
      inputType: 'number',
      min: 0,
      max: 1,
      step: 0.01,
      required: true,
      defaultValue: 0,
    },
    {
      field: 'AttributDeclencheur',
      label: 'Attribut Déclencheur',
      inputType: 'nestedSelect',
      defaultValue: 'Failure',
      options: [
        { label: t ? t('technicalPolicy.attributDeclencheur.Failure') : 'Failure', value: 'Failure' },
        { label: t ? t('technicalPolicy.attributDeclencheur.Operating') : 'Operating', value: 'Operating' },
        { label: t ? t('technicalPolicy.attributDeclencheur.QuasiFailure') : 'Quasi Failure', value: 'QuasiFailure' },
        { label: t ? t('technicalPolicy.attributDeclencheur.Age') : 'Age', value: 'Age' },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.AgeDuringFirstLifecycle') : 'Age During First Lifecycle',
          value: 'AgeDuringFirstLifecycle',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.AgeAfterFirstLifecycle') : 'Age After First Lifecycle',
          value: 'AgeAfterFirstLifecycle',
        },
        { label: t ? t('technicalPolicy.attributDeclencheur.State') : 'State', value: 'State' },
        { label: t ? t('technicalPolicy.attributDeclencheur.Downtime') : 'Downtime', value: 'Downtime' },
        { label: t ? t('technicalPolicy.attributDeclencheur.Quantity') : 'Quantity', value: 'Quantity' },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.CustomDataNumber') : 'Custom DataNumber.*',
          value: 'CustomDataNumber.*',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.CustomDataString') : 'Custom DataString.*',
          value: 'CustomDataString.*',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.InspectionDowntime') : 'Inspection Downtime',
          value: 'InspectionDowntime',
        },
        {
          label: t
            ? t('technicalPolicy.attributDeclencheur.AllPredeterminedProjectsDone')
            : 'All Predetermined Projects Done',
          value: 'AllPredeterminedProjectsDone',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.CorporateCostOfFailure') : 'Corporate Cost Of Failure',
          value: 'CorporateCostOfFailure',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.SocietalCostOfFailure') : 'Societal Cost Of Failure',
          value: 'SocietalCostOfFailure',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.CostOfFailure') : 'Cost Of Failure',
          value: 'CostOfFailure',
        },
        {
          label: t
            ? t('technicalPolicy.attributDeclencheur.SumOfCorporateCostsOfFailure')
            : 'Sum Of Corporate Costs Of Failure',
          value: 'SumOfCorporateCostsOfFailure',
        },
        {
          label: t
            ? t('technicalPolicy.attributDeclencheur.SumOfSocietalCostsOfFailure')
            : 'Sum Of Societal Costs Of Failure',
          value: 'SumOfSocietalCostsOfFailure',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.SumOfCostsOfFailure') : 'Sum Of Costs Of Failure',
          value: 'SumOfCostsOfFailure',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.ConsequenceQuantity') : 'Consequence Quantity.*',
          value: 'ConsequenceQuantity.*',
        },
        {
          label: t
            ? t('technicalPolicy.attributDeclencheur.SumOfConsequenceQuantities')
            : 'Sum Of Consequence Quantities.*',
          value: 'SumOfConsequenceQuantities.*',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.PriorityScore') : 'Priority Score',
          value: 'PriorityScore',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.FinancialValue') : 'Financial Value',
          value: 'FinancialValue',
        },
        {
          label: t ? t('technicalPolicy.attributDeclencheur.EnvironmentAgingRate') : 'Environment Aging Rate',
          value: 'EnvironmentAgingRate',
        },
      ],
      required: true,
    },
    {
      field: 'Operateur',
      label: 'Opérateur',
      inputType: 'select',
      options: [
        { label: '<', value: 'LT' },
        { label: '≤', value: 'LEQ' },
        { label: '>', value: 'GT' },
        { label: '≥', value: 'GEQ' },
      ],
      // Disable if AttributDeclencheur values force to not have operator defined
      disabled: isOperateurDisabled,
      clearIfDisabled: true,
    },
    {
      field: 'ValeurReference',
      label: 'Valeur Référence',
      options: [
        { label: 'Age Ref Delta', value: 'Age Ref Delta' },
        { label: 'Age Ref Pourcentage', value: 'Age Ref Pourcentage' },
        { label: 'CustomDataNumber', value: 'CustomDataNumber' },
        { label: 'ScalarValue', value: 'ScalarValue' },
      ],
      // Disable if AttributDeclencheur values force to not have reference value defined
      disabled: isValeurReferenceDisabled,
      clearIfDisabled: true,
      inputType: (row) => (row?.AttributDeclencheur.startsWith('CustomDataString.') ? 'autocomplete' : 'select'),
      getOptions: attributeValues,
      autocompleteProps: {
        freeSolo: true,
        id: 'criteres-declenchement-valeur-reference-autocomplete',
        checkbox: false,
        disableCloseOnSelect: true,
      },
    },
  ],
  SequencesActions: (
    openCoutsEtDureesModal,
    openTachesEnInterventionModal,
    openImpactsModal,
    categoriesBudgetaires,
    tachesEnIntervention,
    isDisabled,
    isValid
  ) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'SequenceActions', isInvisible: true },
    {
      field: 'IdentifiantAction',
      label: 'Action',
      size: 'medium',
      sx: { width: '150px' },
      disabled: isDisabled,
      valid: isValid,
      required: true,
      errorMessage: "L'identifiant existe déjà",
    },
    {
      field: 'RangExecution',
      label: "Rang d'exécution",
      inputType: 'number',
      min: 1,
      step: 1,
      size: 'small',
      getDefaultValue: (existingRows) => existingRows?.length + 1 || 1,
      required: true,
    },
    {
      field: 'CategoriesBudgetaires',
      label: 'Catégories Budgétaires',
      inputType: 'autocomplete',
      options: categoriesBudgetaires,
      size: 'medium',
      sx: { width: '200px' },
      autocompleteProps: {
        freeSolo: false,
        disableCloseOnSelect: true,
        placeholder: 'Rechercher...',
        checkbox: true,
        isSearchable: true,
        id: 'action-budget-categories-autocomplete',
      },
      info: 'Ce champ permet de rechercher une valeur grâce à une saisie assistée',
    },
    {
      field: 'NiveauDefinitionCouts',
      label: 'Niveau Définition Couts',
      inputType: 'select',
      size: 'small',
      options: [
        { label: 'Action', value: 'Action' },
        { label: 'SubAction', value: 'SubAction' },
      ],
      defaultValue: 'Action',
      required: true,
    },
    {
      field: 'coutEtDurees',
      label: 'Coûts et Durées',
      inputType: 'button',
      buttonLabel: 'Voir/Éditer',
      onClick: openCoutsEtDureesModal,
      size: 'small',
    },
    {
      field: 'impacts',
      label: 'Impacts',
      inputType: 'button',
      buttonLabel: 'Voir/Éditer',
      onClick: openImpactsModal,
      size: 'small',
    },
    {
      field: 'CoefficientModificationCouts',
      label: 'Coefficients Modification Coûts',
      inputType: 'listOfFloat',
      size: 'small',
    },
    {
      field: 'CoefficientModificationDuree',
      label: 'Coefficients Modification Durée',
      size: 'small',
      inputType: 'listOfFloat',
    },
    { field: 'ActionLocale', label: 'Action Locale', inputType: 'checkbox', size: 'small' },
    { field: 'ConsignationRequise', label: 'Consignation Requise', inputType: 'checkbox', size: 'small' },
    { field: 'CapitalisationCharges', label: 'Capitalisation Charges', inputType: 'checkbox', size: 'small' },
    {
      field: 'TachesEnIntervention',
      label: 'Tâches en Intervention',
      inputType: 'autocomplete',
      withButton: true,
      buttonLabel: 'Voir',
      onClick: openTachesEnInterventionModal,
      options: tachesEnIntervention,
      size: 'small',
      autocompleteProps: {
        freeSolo: false,
        disableCloseOnSelect: true,
        placeholder: 'Rechercher...',
        checkbox: true,
        isSearchable: true,
        id: 'taches-intervention-autocomplete',
      },
      info: 'Ce champ permet de rechercher une valeur grâce à une saisie assistée',
    },
  ],
  CoutsEtDureesActions: (costLevel, family, niveauDefinitionCout) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'FamilleEquipement',
      label: 'Famille Équipement',
      inputType: 'text',
      options: [{ label: 'Aucune', value: '' }, ...(family ?? [])],
      isInvisible: niveauDefinitionCout === 'Action',
      disabled: true,
      required: true,
    },
    {
      field: 'NiveauCouts',
      label: 'Niveau de coût',
      inputType: 'text',
      options: costLevel,
      disabled: true, // This field is intentionally disabled because it is not meant to be edited by the user.
      required: true,
    },
    {
      field: 'TypeCout',
      label: 'Type Coût',
      inputType: 'select',
      options: ['Strict', 'Complexe'],
      required: true,
      disabled: true,
    },
    { field: 'CoutUnitaire', label: 'Coût Unitaire (k€)', inputType: 'number', min: 0, required: true },
    { field: 'CoutFixe', label: 'Coût Fixe (k€)', inputType: 'number', min: 0, required: true },
    { field: 'DureeUnitaire', label: 'Durée Unitaire (jours)', inputType: 'number', min: 0, required: true },
    { field: 'DureeFixe', label: 'Durée Fixe (jours)', inputType: 'number', min: 0, required: true },
  ],
  TachesEnIntervention: (tachesEnIntervention) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'Identifiant',
      label: 'Tâche en intervention',
      inputType: 'text',
      options: tachesEnIntervention ?? [],
      disabled: true,
    },
    {
      field: 'Competence',
      label: 'Competence',
      inputType: 'text',
    },
    {
      field: 'DureeRelative',
      label: 'Durée relative',
      inputType: 'number',
    },
    {
      field: 'ETPRequis',
      label: 'ETP Requis',
      inputType: 'number',
    },
  ],
  Impacts: (family) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'FamilleEquipementImpactee',
      label: 'Famille Équipement',
      inputType: 'text',
      options: family,
      disabled: true, // This field is intentionally disabled because it is not meant to be edited by the user.
      required: true,
    },
    {
      field: 'Type',
      label: 'Type',
      inputType: 'select',
      options: [
        { label: 'NoAction', value: 'NoAction' },
        { label: 'Repairs', value: 'Repairs' },
        { label: 'Replacement', value: 'Replacement' },
        { label: 'Inspection', value: 'Inspection' },
        { label: 'Activation', value: 'Activation' },
        { label: 'Deactivation', value: 'Deactivation' },
        { label: 'RepairsWithoutInspection', value: 'RepairsWithoutInspection' },
        { label: 'Refurbishment', value: 'Refurbishment' },
      ],
      required: true,
    },
    {
      field: 'Impact',
      label: 'Impact',
      inputType: 'number',
      min: 0,
      max: 1,
      step: 0.01,
      info: 'valeur entre 0 et 1',
      disabled: (row) =>
        row.Type === 'NoAction' ||
        row.Type === 'Replacement' ||
        row.Type === 'Inspection' ||
        row.Type === 'Activation' ||
        row.Type === 'Deactivation',
    },
    { field: 'ImpactPriorisation', label: 'Impact sur la priorité', inputType: 'checkbox' },
    {
      field: 'FamilleEquipementRetrofit',
      label: 'Famille Retrofit',
      inputType: 'select',
      options: (row) => {
        const baseOptions = [{ label: 'Aucune', value: '' }, ...(family ?? [])];
        if (row.Type === 'Replacement' || row.Type === 'Refurbishment') {
          return baseOptions.filter(
            (opt) => !row.FamilleEquipementImpactee || opt.value !== row.FamilleEquipementImpactee
          );
        }
        return baseOptions;
      },
      disabled: (row) =>
        row.Type === 'NoAction' ||
        row.Type === 'Repairs' ||
        row.Type === 'RepairsWithoutInspection' ||
        row.Type === 'Activation' ||
        row.Type === 'Deactivation' ||
        row.Type === 'Inspection',
    },
  ],
  GestionActivites: [
    'ReglesGestion',
    'CriteresDeclenchement',
    'Impacts',
    'Gisement',
    'ClasseActif',
    'id',
    'SequencesActionsImposees',
    'CoutsEtDureesActions',
    'SequencesActions',
  ],
  SequencesActionsImposees: [
    'DateRealisation',
    'NiveauDePriorisation',
    'Groupe',
    'Identifiant',
    'SequenceActions',
    'CategoriesBudgetaires',
  ],
  RequiredSequencesActionsImposees: ['DateRealisation', 'Groupe', 'Identifiant', 'SequenceActions'],
};
